/**
 * Classement des écritures bancaires vers les sous-postes du budget.
 *
 * Le moteur de règles est pur. Les amorces (`SOUS_POSTES_INITIAUX`,
 * `REGLES_INITIALES`) traduisent fidèlement les tables de rattachement
 * historiques : appliquées seules, elles reproduisent l'exécution d'avant le
 * classement dynamique (voir `non-regression-classement.test.ts`). Elles
 * servent à amorcer la base et de repli quand le module est inactif.
 */
import { COMPTES_PERSONNEL, NOMENCLATURE, RATTACHEMENT_PRODUITS, REGROUPEMENTS_AUTRES_CHARGES, REGROUPEMENTS_PERSONNEL, poste, } from './configuration.js';
import { codeCompte } from './ecritures.js';
/** Poste technique des écritures hors budget voté. */
export const POSTE_HORS_BUDGET = 'hors_budget';
/** Clé des écritures sans classement ; ligne des sorties à classer. */
export const A_CLASSER = 'a_classer';
/**
 * Clé de la ligne des entrées à classer : distincte de celle des sorties, pour
 * que la traçabilité d'une ligne n'en mêle pas l'autre. Son `posteParent`
 * reste `A_CLASSER`.
 */
export const A_CLASSER_RECETTES = 'a_classer_recettes';
export function sensEcriture(e) {
    return e.sens === 'debit' ? 'recette' : 'depense';
}
export function familleSousPoste(s) {
    if (s.poste === POSTE_HORS_BUDGET)
        return 'hors_budget';
    const p = poste(s.poste);
    if (!p)
        return 'hors_budget';
    if (p.nature === 'produit')
        return 'produit';
    return p.categorie === 'Charges de personnel' ? 'personnel' : 'charge';
}
/** Retire les accents (diacritiques combinants) sans toucher au reste. */
function sansAccents(s) {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
export function normaliser(s) {
    return sansAccents(s)
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
function valeurDuChamp(champ, e) {
    switch (champ) {
        case 'tiers':
            return e.tiers ?? '';
        case 'libelle':
            return e.libelle;
        case 'compte':
            return codeCompte(e) ?? '';
        case 'compte_libelle':
            return e.compteLibelle;
        case 'type_transaction':
            return e.typeOperation;
    }
}
export function regleCorrespond(r, e) {
    if (!r.actif)
        return false;
    if (r.sens !== 'tous' && r.sens !== sensEcriture(e))
        return false;
    const attendu = normaliser(r.valeur);
    if (attendu === '')
        return false;
    const v = normaliser(valeurDuChamp(r.champ, e));
    switch (r.operateur) {
        case 'egal':
            return v === attendu;
        case 'contient':
            return v.includes(attendu);
        case 'commence_par':
            return v.startsWith(attendu);
    }
}
/** Règles dans l'ordre d'évaluation : priorité croissante, puis identifiant. Copie triée. */
export function trierRegles(regles) {
    return [...regles].sort((a, b) => a.priorite - b.priorite || a.id - b.id);
}
/** Première règle qui correspond, quel que soit l'ordre des règles reçues. */
export function premiereRegle(regles, e) {
    return trierRegles(regles).find((r) => regleCorrespond(r, e));
}
export function classerParRegles(ecritures, regles, sousPostes) {
    const tries = trierRegles(regles);
    const affectations = new Map();
    for (const e of ecritures) {
        const r = tries.find((x) => regleCorrespond(x, e));
        if (r)
            affectations.set(e.id, r.sousPoste);
    }
    return { sousPostes, affectations };
}
// ── Amorces ────────────────────────────────────────────────────────────────
/** Descriptions transmises à Jev : ce qui distingue chaque sous-poste. */
const DESCRIPTIONS = {
    cotisations: 'Cotisation annuelle versée par une compagnie, une structure ou une personne adhérente du SCC (virements ' +
        'mentionnant « cotisation », « adhésion », « COT-2026 », paiements de factures de cotisation).',
    fcap_ccneac: 'Versement du FCAP de la convention collective CCNEAC (fonds de financement du paritarisme).',
    fcap_ccnsvp: 'Versement du FCAP de la convention collective CCNSVP (spectacle vivant privé).',
    dgca_professionnalisation: 'Subvention du ministère de la Culture, DGCA, au titre de la professionnalisation (virement de la DRFIP ou du Trésor public).',
    dgca_theatre: 'Subvention du ministère de la Culture, DGCA, au titre du théâtre.',
    audiens: 'Versement d’Audiens au titre de la convention de partenariat.',
    remboursements_frais: 'Remboursement de frais avancés (note de frais remboursée, avoir fournisseur).',
    autres_produits: 'Autre produit ponctuel sans lien avec les ressources budgétées.',
    remboursements_prets: 'Remboursement d’un prêt consenti, retour de fonds prêtés.',
    depots_divers: 'Dépôt divers, encaissement en espèces ou chèque non identifié.',
    apports: 'Apport en fonds propres.',
    produits_financiers: 'Intérêts et produits financiers du livret ou du compte.',
    autres_encaissements: 'Encaissement hors budget qui ne relève d’aucune autre catégorie.',
    salaires_pas: 'Salaire net versé à un salarié du SCC et prélèvement à la source.',
    cotisations_sociales: 'Cotisations sociales : URSSAF, caisses de retraite complémentaire, prévoyance, mutuelle, France Travail.',
    cheques_dejeuner: 'Chèques déjeuner ou titres-restaurant (part employeur).',
    location_siege: 'Loyer et charges des locaux du siège du SCC.',
    location_instances: 'Location de salles pour les réunions du Conseil national et des instances.',
    abonnements: 'Abonnements logiciels : Zoho, outils numériques, logiciels d’intelligence artificielle.',
    fournitures_site: 'Fournitures de bureau, site internet, hébergement, noms de domaine, téléphonie.',
    ag_receptions: 'Assemblée générale, repas, réceptions et frais de bouche.',
    deplacements: 'Déplacements : train, avion, location de voiture, carburant, péages, hôtels.',
    autres_postes: 'Autre charge de fonctionnement : frais bancaires, assurances, honoraires, électricité, affranchissement, publicité, colloques.',
    depenses_hors_budget: 'Sortie qui n’est pas une charge du budget : virement interne entre comptes, TVA, remboursement d’emprunt, avance.',
};
function desc(id) {
    return DESCRIPTIONS[id] ?? '';
}
/**
 * Sous-postes initiaux. L'ordre reproduit l'ordre d'affichage historique ; les
 * identifiants des charges reprennent les clés des regroupements, de sorte que
 * l'exécution garde ses clés (`salaires_pas`, `location_siege`…).
 */
export const SOUS_POSTES_INITIAUX = (() => {
    const out = [];
    let ordre = 0;
    const ajouter = (id, posteCode, libelle, sens) => {
        ordre += 10;
        out.push({ id, poste: posteCode, libelle, description: desc(id), sens, ordre, archive: false });
    };
    for (const p of NOMENCLATURE.filter((x) => x.nature === 'produit'))
        ajouter(p.code, p.code, p.libelle, 'recette');
    for (const r of Object.values(RATTACHEMENT_PRODUITS)) {
        if (!r.budgete)
            ajouter(r.poste, POSTE_HORS_BUDGET, r.libelle, 'recette');
    }
    ajouter('autres_encaissements', POSTE_HORS_BUDGET, 'Autres encaissements', 'recette');
    for (const g of REGROUPEMENTS_PERSONNEL) {
        const p = NOMENCLATURE.find((x) => x.rattachementExecution === g.cle);
        ajouter(g.cle, p?.code ?? 'salaires_bruts', g.libelle, 'depense');
    }
    for (const g of REGROUPEMENTS_AUTRES_CHARGES)
        ajouter(g.cle, g.cle, g.libelle, 'depense');
    ajouter('autres_postes', 'autres_postes', 'Autres postes', 'depense');
    ajouter('depenses_hors_budget', POSTE_HORS_BUDGET, 'Dépenses hors budget', 'depense');
    return out;
})();
/**
 * Règles initiales, dans l'ordre de priorité qui reproduit `fluxExercice()`
 * historique : type d'opération pour les entrées, codes de compte du personnel,
 * puis codes et libellés des regroupements d'autres charges.
 *
 * Deux écarts assumés avec le rattachement historique, sans effet sur
 * l'instantané livré :
 *   — une entrée d'un type Zoho inconnu n'a pas de règle : elle va sur la
 *     ligne « À classer » des recettes (`a_classer_recettes`) et non plus
 *     dans « Autres encaissements » ;
 *   — toute comparaison, y compris sur le libellé du compte
 *     (`compte_libelle`) et le code de compte, ignore la casse, les accents
 *     et les espaces superflus, là où l'ancien code exigeait l'égalité exacte.
 */
export const REGLES_INITIALES = (() => {
    const out = [];
    const ajouter = (champ, operateur, valeur, sens, sousPoste) => {
        out.push({ id: out.length + 1, priorite: (out.length + 1) * 10, champ, operateur, valeur, sens, sousPoste, actif: true });
    };
    for (const [type, r] of Object.entries(RATTACHEMENT_PRODUITS))
        ajouter('type_transaction', 'egal', type, 'recette', r.poste);
    for (const g of REGROUPEMENTS_PERSONNEL) {
        for (const c of g.comptes)
            ajouter('compte', 'egal', c, 'depense', g.cle);
    }
    // Garde-fou : tout compte de personnel est couvert par un regroupement.
    for (const c of COMPTES_PERSONNEL) {
        if (!REGROUPEMENTS_PERSONNEL.some((g) => g.comptes.includes(c)))
            ajouter('compte', 'egal', c, 'depense', 'salaires_pas');
    }
    for (const g of REGROUPEMENTS_AUTRES_CHARGES) {
        for (const c of g.comptes)
            ajouter('compte', 'egal', c, 'depense', g.cle);
        for (const l of g.libelles)
            ajouter('compte_libelle', 'egal', l, 'depense', g.cle);
    }
    return out;
})();
// ── Proposition de règle après un arbitrage ───────────────────────────────
const PREFIXES_BANCAIRES = /^(vir(ement)?( inst(antane)?)?( sepa)?( recu| emis)?( de)?|prlv( sepa)?|prelevement( sepa)?|cb|carte( \d+)?|cheque|chq|remise( cheque)?|paiement( cb)?)\s+/i;
/**
 * Critère proposé pour « toujours classer… » : le tiers s'il est connu, sinon
 * le cœur du libellé bancaire (sans préfixe d'opération ni référence), sinon le
 * libellé du compte de contrepartie.
 */
export function proposerRegle(e) {
    const tiers = (e.tiers ?? '').trim();
    if (tiers !== '')
        return { champ: 'tiers', operateur: 'contient', valeur: tiers };
    const l = e.libelle.trim().replace(/\s+/g, ' ');
    if (l !== '') {
        // Le préfixe se repère sans accents (« PRÉLÈVEMENT SEPA ») ; on retire
        // autant de mots du libellé d'origine, dont la casse et les accents restent.
        const prefixe = PREFIXES_BANCAIRES.exec(sansAccents(l));
        const tous = l.split(' ');
        const mots = prefixe ? tous.slice(prefixe[0].trim().split(' ').length) : tous;
        // Coupe à la première référence : un mot contenant un chiffre.
        const coupe = mots.findIndex((m) => /\d/.test(m));
        const coeur = (coupe === -1 ? mots : mots.slice(0, coupe)).join(' ').trim();
        if (coeur.length >= 3)
            return { champ: 'libelle', operateur: 'contient', valeur: coeur };
    }
    const compte = e.compteLibelle.trim();
    if (compte !== '')
        return { champ: 'compte_libelle', operateur: 'egal', valeur: compte };
    return null;
}
/** Empreinte des champs utilisés pour classer : un changement dans Zoho la modifie. */
export function empreinteEcriture(e) {
    const s = [e.date, e.montant, e.sens, e.typeOperation, e.libelle, e.compteCode, e.compteLibelle, e.tiers ?? ''].join('␟');
    // FNV-1a 32 bits : pur, sans dépendance, suffisant pour détecter un changement.
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
//# sourceMappingURL=classement.js.map